/**
 * Web image-gating utilities — mirror of /app/mobile/src/utils/image.ts.
 * Mobile is the source of truth; this file MUST stay semantically
 * identical so that web and mobile render the same hero URL given the
 * same listing payload.
 *
 * Spec (2026-06-10 follow-up to Bug C fix):
 *
 *   1. If ANY valid URL exists for a listing → render an image,
 *      regardless of `image_health`. `unchecked` listings are NEVER
 *      hidden; an optional "UNVERIFIED" overlay badge can be drawn on
 *      top of the rendered image.
 *
 *   2. The placeholder ("no image" / "image broken") is shown ONLY
 *      when there is no valid URL to render — either no primary AND no
 *      images[], or `image_health == "dead_on_check"` with no
 *      alternative URL in `images[]`.
 *
 *   3. The thumbnail strip on the detail page must always be able to
 *      swap the hero image, even when the listing is `unchecked` —
 *      there is no `image_health` gate on the rendered <img />.
 *
 *   4. Backward-compat: a missing/`null` `image_health` field is
 *      treated like `"unchecked"` (image shown, badge optional).
 */

/** Non-empty string URL predicate. */
export const hasValidUrl = (url) =>
  typeof url === "string" && url.trim().length > 0;

/** De-duped list of every valid URL the listing carries. Primary first. */
export const collectImageUrls = (listing) => {
  const out = [];
  const seen = new Set();
  const push = (u) => {
    if (hasValidUrl(u) && !seen.has(u)) {
      seen.add(u);
      out.push(u);
    }
  };
  push(listing?.primary_image_url);
  if (Array.isArray(listing?.images)) {
    for (const u of listing.images) push(u);
  }
  return out;
};

/**
 * Pick the best initial hero URL. Prefers the explicit primary unless
 * `image_health === "dead_on_check"`, in which case it falls back to
 * the first alternative in `images[]`. Returns `null` only when there
 * is genuinely no valid URL to render.
 */
export const pickHeroUrl = (listing) => {
  const all = collectImageUrls(listing);
  if (all.length === 0) return null;
  if (listing?.image_health === "dead_on_check") {
    const primary = hasValidUrl(listing?.primary_image_url)
      ? listing.primary_image_url
      : null;
    const alt = all.find((u) => u !== primary);
    // No alternative — caller will render placeholder.
    return alt ?? null;
  }
  if (hasValidUrl(listing?.primary_image_url)) return listing.primary_image_url;
  return all[0];
};

/**
 * Should the "UNVERIFIED" overlay badge be drawn on top of the
 * rendered image? True for `unchecked` listings and for listings
 * whose `image_health` field is missing entirely (legacy / backend
 * pre-M5 records). Never true once the worker has labelled the image
 * as `alive` or `dead_on_check`.
 */
export const shouldShowUnverifiedBadge = (image_health) =>
  image_health == null || image_health === "unchecked";
